import { z, type ZodTypeAny } from "zod"
import { zodToJsonSchema } from "zod-to-json-schema"
import type { ClientToolCallNotification, ClientToolManifest, JsonValue, PermissionMode } from "./generated"

export type ClientToolTurnMode = "full" | "read-only"

export interface ClientToolContext {
    sessionId: string
    turnId: string
    mode: ClientToolTurnMode
    permissionMode: PermissionMode
    signal: AbortSignal
}

export interface ClientToolDefinition<TSchema extends ZodTypeAny> {
    name: string
    description: string
    schema: TSchema
}

export type ClientToolCallback<TSchema extends ZodTypeAny> = (input: z.output<TSchema>, context: ClientToolContext) => JsonValue | Promise<JsonValue>

export type ClientToolContentBlock =
    | { type: "text"; text: string }
    | {
          type: "image"
          data: string
          mimeType: "image/png" | "image/jpeg" | "image/webp"
      }

function contentBlockValue(block: ClientToolContentBlock): JsonValue {
    if (block.type === "text") return { type: "text", text: block.text }
    return { type: "image", data: block.data, mimeType: block.mimeType }
}

export function clientToolContent(content: ClientToolContentBlock[], structuredContent?: JsonValue): JsonValue {
    const blocks: JsonValue[] = content.map(contentBlockValue)
    const result: Record<string, JsonValue> = {
        __wildlarkClientToolResult: "mcp-content-v1",
        content: blocks,
    }
    if (structuredContent !== undefined) result.structuredContent = structuredContent
    return result
}

interface RegisteredClientTool {
    manifest: ClientToolManifest["tools"][number]
    run(input: unknown, context: ClientToolContext): Promise<JsonValue>
}

let nextRegistryId = 1

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function jsonValue(value: unknown): JsonValue {
    if (value === null || typeof value === "string" || typeof value === "boolean") return value
    if (typeof value === "number") {
        if (!Number.isFinite(value)) throw new Error("Client tool schemas and results must contain finite JSON numbers")
        return value
    }
    if (Array.isArray(value)) return value.map(jsonValue)
    if (isRecord(value)) {
        const result: Record<string, JsonValue> = {}
        for (const [key, item] of Object.entries(value)) {
            if (item !== undefined) result[key] = jsonValue(item)
        }
        return result
    }
    throw new Error("Client tool schemas and results must be JSON values")
}

export class ClientToolRegistry {
    private readonly registryId = nextRegistryId++
    private readonly tools = new Map<string, RegisteredClientTool>()
    private revision = 0

    register<TSchema extends ZodTypeAny>(definition: ClientToolDefinition<TSchema>, callback: ClientToolCallback<TSchema>): () => void {
        if (this.tools.has(definition.name)) {
            throw new Error(`Client tool ${definition.name} is already registered`)
        }
        const inputSchema = jsonValue(
            zodToJsonSchema(definition.schema, {
                $refStrategy: "none",
                target: "jsonSchema7",
            })
        )
        if (!isRecord(inputSchema) || inputSchema.type !== "object") {
            throw new Error(`Client tool ${definition.name} must use an object schema`)
        }

        const registration: RegisteredClientTool = {
            manifest: {
                name: definition.name,
                description: definition.description,
                inputSchema,
            },
            run: async (input, context) => jsonValue(await callback(definition.schema.parse(input), context)),
        }
        this.tools.set(definition.name, registration)
        this.revision += 1

        let registered = true
        return () => {
            if (!registered || this.tools.get(definition.name) !== registration) return
            registered = false
            this.tools.delete(definition.name)
            this.revision += 1
        }
    }

    manifest(): ClientToolManifest | undefined {
        if (this.tools.size === 0) return undefined
        return {
            toolsetId: `client-tools-${this.registryId}-${this.revision}`,
            tools: [...this.tools.values()].map((tool) => tool.manifest),
        }
    }

    attachToTurn(method: string, params: unknown): unknown {
        const manifest = this.manifest()
        if (
            (method !== "session.turn.start" &&
                method !== "wildlark.task.turn.start" &&
                method !== "wildlark.task.session.send" &&
                method !== "wildlark.task.handoff") ||
            !manifest ||
            !isRecord(params)
        )
            return params
        // Product client tools are intentionally all-or-nothing. Every turn gets
        // the same registered manifest: per-turn subsets fragment the provider's
        // prompt cache and make delayed queued turns depend on transient UI state.
        const options = isRecord(params.options) ? params.options : {}
        return {
            ...params,
            options: {
                ...options,
                clientTools: manifest,
            },
        }
    }

    async call(params: ClientToolCallNotification, signal: AbortSignal): Promise<JsonValue> {
        const manifest = this.manifest()
        if (!manifest || params.toolsetId !== manifest.toolsetId) {
            throw new Error("The client toolset is no longer available")
        }
        const tool = this.tools.get(params.name)
        if (!tool) throw new Error(`Unknown client tool: ${params.name}`)
        return tool.run(params.arguments, {
            sessionId: params.sessionId,
            turnId: params.turnId,
            mode: params.mode === "read-only" ? "read-only" : "full",
            permissionMode:
                params.permissionMode === "ask" || params.permissionMode === "yolo" || params.permissionMode === "auto" ? params.permissionMode : "auto",
            signal,
        })
    }
}
